/**
 * Regional revenue & Collection (UC-S0) — pure helpers ported from ExecutiveDashboard.
 */

import type { RegionSummaryRow } from '../types/regionalPerformance'

export type { RegionSummaryRow } from '../types/regionalPerformance'

export interface RegionalPerformanceRow {
  region?: string
  calendar_year?: number
  calendar_month?: number
  financial_year?: number
  financial_quarter?: number
  financial_year_period?: string
  total_clients?: number
  total_invoiced_usd?: string | number
  total_revenue_usd?: string | number
  total_outstanding_usd?: string | number
  total_invoiced_original_currency?: string | number
  /** Paid / collected in original (regional) currency — summed for display when not USD. */
  total_paid_original_currency?: string | number
  /** When present (ISO 4217), overrides default currency for this region row. */
  region_currency?: string
  enterprise_clients?: number
  strategic_clients?: number
  standard_clients?: number
  small_clients?: number
  revenue_market_share_percent?: number
  clients_with_outstanding?: number
  client_list_summary?: string
  /** JSON array: contact_id → billing account (UC-S10 entity performance). */
  billing_account_info?: string
  total_invoices_raised?: string | number
  active_clients?: number
}

export interface ParsedRegionalClient {
  clientId: string
  name: string
  tier: string
  currency: string
  revenueUSD: number
  invoicedUSD: number
  /** Paid / collected: original amount when currency ≠ USD (pipe field 7), else USD paid. */
  revenueOriginal: number
  /** Invoiced: original amount when currency ≠ USD (pipe field 6), else USD invoiced. */
  invoicedOriginal: number
  /** Outstanding in USD (field 5) — KPIs and % region denominator. */
  outstanding: number
  /** Outstanding for display: local when derivable, else same as USD. */
  outstandingOriginal: number
  payerScore: string
  monthCount?: number
}

export interface RegionChartRow {
  region: string
  regionName: string
  revenue: number
  outstanding: number
  totalClients: number
  collectionRate: number
}

export interface SelectedRegionAggregate {
  region: string
  total_clients: number
  enterprise_clients?: number
  strategic_clients?: number
  standard_clients?: number
  small_clients?: number
  total_revenue_usd: number
  total_outstanding_usd: number
  total_invoiced_usd: number
  total_invoiced_original_currency: number
  revenue_market_share_percent?: number
  clients_with_outstanding?: number
  client_list_summary: string
  clientListItems: string[]
  aggregatedClients?: ParsedRegionalClient[]
}

export function getCurrentIndianFinancialYearPeriod(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = d.getMonth() + 1
  const secondPart = m >= 4 ? (y + 1) % 100 : y % 100
  const firstPart = m >= 4 ? y : y - 1
  return `${firstPart}-${String(secondPart).padStart(2, '0')}`
}

export const REGION_DISPLAY_NAMES: Record<string, string> = {
  US: 'United States',
  GB: 'United Kingdom',
  AU: 'Australia',
  CA: 'Canada',
  AE: 'United Arab Emirates',
  UK: 'United Kingdom',
  SG: 'Singapore',
  IN: 'India',
  QA: 'Qatar',
  ZA: 'South Africa',
  MY: 'Malaysia',
  TH: 'Thailand',
  GI: 'Gibraltar',
  DE: 'Germany',
  HK: 'Hong Kong',
  IE: 'Ireland',
  NL: 'Netherlands',
  JM: 'Jamaica',
  PR: 'Puerto Rico',
  NZ: 'New Zealand',
  VG: 'British Virgin Islands',
  NG: 'Nigeria',
  LI: 'Liechtenstein',
  JP: 'Japan',
  DK: 'Denmark',
  PH: 'Philippines',
}

export const REGION_FLAGS: Record<string, string> = {
  US: '🇺🇸',
  GB: '🇬🇧',
  AU: '🇦🇺',
  CA: '🇨🇦',
  AE: '🇦🇪',
  UK: '🇬🇧',
  SG: '🇸🇬',
  IN: '🇮🇳',
  QA: '🇶🇦',
  ZA: '🇿🇦',
  MY: '🇲🇾',
  TH: '🇹🇭',
  GI: '🇬🇮',
  DE: '🇩🇪',
  HK: '🇭🇰',
  IE: '🇮🇪',
  NL: '🇳🇱',
  JM: '🇯🇲',
  PR: '🇵🇷',
  NZ: '🇳🇿',
  VG: '🇻🇬',
  NG: '🇳🇬',
  LI: '🇱🇮',
  JP: '🇯🇵',
  DK: '🇩🇰',
  PH: '🇵🇭',
}

/** Default reporting currency per region code (UK includes GB). Fallback: USD. */
export const REGION_LOCAL_CURRENCY: Record<string, string> = {
  US: 'USD',
  UK: 'GBP',
  GB: 'GBP',
  AU: 'AUD',
  CA: 'CAD',
  NZ: 'NZD',
  SG: 'SGD',
  IN: 'INR',
  AE: 'AED',
  QA: 'QAR',
  ZA: 'ZAR',
  MY: 'MYR',
  TH: 'THB',
  GI: 'GIP',
  DE: 'EUR',
  HK: 'HKD',
  IE: 'EUR',
  NL: 'EUR',
  JM: 'JMD',
  PR: 'USD',
  VG: 'USD',
  NG: 'NGN',
  LI: 'CHF',
  JP: 'JPY',
  DK: 'DKK',
  PH: 'PHP',
  Other: 'USD',
}

export const REGION_MAP_POSITION: Record<string, { top: string; left: string }> = {
  US: { top: '35%', left: '20%' },
  CA: { top: '25%', left: '18%' },
  GB: { top: '25%', left: '48%' },
  UK: { top: '25%', left: '48%' },
  DE: { top: '28%', left: '52%' },
  NL: { top: '25%', left: '50%' },
  IE: { top: '28%', left: '45%' },
  AE: { top: '40%', left: '62%' },
  QA: { top: '42%', left: '60%' },
  IN: { top: '45%', left: '72%' },
  SG: { top: '58%', left: '78%' },
  MY: { top: '55%', left: '76%' },
  TH: { top: '50%', left: '75%' },
  PH: { top: '48%', left: '80%' },
  HK: { top: '42%', left: '82%' },
  JP: { top: '38%', left: '88%' },
  AU: { top: '75%', left: '85%' },
  NZ: { top: '80%', left: '92%' },
  ZA: { top: '70%', left: '55%' },
  NG: { top: '50%', left: '52%' },
  VG: { top: '45%', left: '25%' },
  JM: { top: '45%', left: '23%' },
  PR: { top: '45%', left: '28%' },
  GI: { top: '35%', left: '47%' },
  LI: { top: '30%', left: '52%' },
  DK: { top: '22%', left: '52%' },
}

const MONTH_NAMES: Record<number, string> = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}

export function monthLabel(m: number): string {
  return MONTH_NAMES[m] || `Month ${m}`
}

export function parseClientListSummary(clientsString: string | undefined): ParsedRegionalClient[] {
  if (!clientsString) return []
  return clientsString
    .split(' ~~ ')
    .map(row => row.trim())
    .filter(Boolean)
    .map(row => {
      const parts = row.split('|').map(p => p.trim())
      if (parts.length < 10) return null
      const clientId = parts[0]
      const name = parts[1] || 'Unknown'
      const tier = parts[2] || 'Standard'
      const invoicedUSD = parseFloat((parts[3] || '').replace(/,/g, '')) || 0
      const paidUSD = parseFloat((parts[4] || '').replace(/,/g, '')) || 0
      const outstandingUsd = parseFloat((parts[5] || '').replace(/,/g, '')) || 0
      const currencyToken = parts[9] && /^[A-Z]{3}$/.test(parts[9]) ? parts[9] : 'USD'
      const part6 = parseFloat((parts[6] || '').replace(/,/g, '')) || 0
      const part7 = parseFloat((parts[7] || '').replace(/,/g, '')) || 0
      const revenueOriginal =
        currencyToken !== 'USD' ? part7 || paidUSD : paidUSD
      const invoicedOriginal =
        currencyToken !== 'USD' ? part6 || invoicedUSD : invoicedUSD
      let outstandingOriginal: number
      if (currencyToken === 'USD') {
        outstandingOriginal = outstandingUsd
      } else if (outstandingUsd > 0 && part6 > 0) {
        outstandingOriginal = part6
      } else if (outstandingUsd > 0) {
        const paidLoc = part7 || paidUSD
        outstandingOriginal =
          paidUSD > 0 && paidLoc > 0
            ? outstandingUsd * (paidLoc / paidUSD)
            : outstandingUsd
      } else {
        outstandingOriginal = 0
      }
      const payerScore =
        parts.length >= 12 ? (parts[11] || 'Unknown') : (parts[parts.length - 1] || 'Unknown')
      return {
        clientId,
        name,
        tier,
        currency: currencyToken,
        revenueUSD: paidUSD,
        invoicedUSD,
        revenueOriginal,
        invoicedOriginal,
        outstanding: outstandingUsd,
        outstandingOriginal,
        payerScore,
      }
    })
    .filter((c): c is ParsedRegionalClient => c != null && c.name !== 'Unknown')
    .sort((a, b) => b.revenueUSD - a.revenueUSD)
}

export function extractFinancialYearPeriods(data: RegionalPerformanceRow[]): string[] {
  const set = new Set<string>()
  for (const item of data) {
    if (item.financial_year_period != null) set.add(String(item.financial_year_period))
  }
  return [...set].sort((a, b) => b.localeCompare(a))
}

export function extractCalendarMonths(data: RegionalPerformanceRow[]): number[] {
  const set = new Set<number>()
  for (const item of data) {
    if (item.calendar_month != null) set.add(Number(item.calendar_month))
  }
  return [...set].sort((a, b) => a - b)
}

export function regionsSortedByRevenue(data: RegionalPerformanceRow[]): string[] {
  const revenue: Record<string, number> = {}
  for (const item of data) {
    const r = item.region
    if (!r) continue
    revenue[r] = (revenue[r] || 0) + parseFloat(String(item.total_revenue_usd || 0))
  }
  return [...new Set(data.map(i => i.region).filter(Boolean) as string[])].sort(
    (a, b) => (revenue[b] || 0) - (revenue[a] || 0)
  )
}

export type QuarterKey = 'all' | 'q1' | 'q2' | 'q3' | 'q4'

export function filterByTimePeriod(
  data: RegionalPerformanceRow[],
  selectedYear: string,
  selectedQuarter: QuarterKey,
  selectedMonth: string
): RegionalPerformanceRow[] {
  return data.filter(item => {
    if (!item) return false

    let yearMatch = true
    if (selectedYear !== 'all') {
      yearMatch = false
      if (item.financial_year_period && item.financial_year_period === selectedYear) {
        yearMatch = true
      } else if (
        item.financial_year != null &&
        String(item.financial_year) === selectedYear
      ) {
        yearMatch = true
      } else if (
        item.calendar_year != null &&
        String(item.calendar_year) === selectedYear
      ) {
        yearMatch = true
      }
    }

    let quarterMatch = true
    if (selectedQuarter !== 'all') {
      const quarterNum = parseInt(selectedQuarter.replace('q', ''), 10)
      quarterMatch = item.financial_quarter === quarterNum
    }

    let monthMatch = true
    if (selectedQuarter === 'all' && selectedMonth !== 'all') {
      monthMatch = item.calendar_month === parseInt(selectedMonth, 10)
    }

    return yearMatch && quarterMatch && monthMatch
  })
}

/** UK chip aggregates rows coded as UK or GB in source data. */
export function rowMatchesRegionFilter(
  selectedRegion: string,
  rowRegion: string | undefined
): boolean {
  if (!rowRegion) return false
  if (selectedRegion === 'US') return rowRegion === 'US'
  if (selectedRegion === 'UK') return rowRegion === 'UK' || rowRegion === 'GB'
  return rowRegion === selectedRegion
}

export function filterByRegion(
  timeFiltered: RegionalPerformanceRow[],
  selectedRegion: string
): RegionalPerformanceRow[] {
  if (selectedRegion === 'all') return timeFiltered
  return timeFiltered.filter(
    item => item && rowMatchesRegionFilter(selectedRegion, item.region)
  )
}

/** Sorted codes that appear only under Others (US / UK+GB are top chips). */
export function otherRegionsForPicker(availableSorted: string[]): string[] {
  return availableSorted.filter(r => r !== 'US' && r !== 'UK' && r !== 'GB')
}

export function countUniqueClientIds(filteredData: RegionalPerformanceRow[]): number {
  const ids = new Set<string>()
  for (const item of filteredData) {
    if (!item.client_list_summary) continue
    for (const c of parseClientListSummary(item.client_list_summary)) {
      if (c.clientId) ids.add(c.clientId)
    }
  }
  return ids.size
}

export function computeTotalMetrics(filteredData: RegionalPerformanceRow[]): {
  totalClients: number
  activeClients: number
  totalRevenue: number
  totalOutstanding: number
  totalInvoiced: number
  totalInvoicedOriginal: number
} {
  const unique = countUniqueClientIds(filteredData)
  const acc = filteredData.reduce(
    (a, region) => ({
      totalClients: unique || a.totalClients,
      activeClients: a.activeClients + (region.active_clients || 0),
      totalRevenue: a.totalRevenue + parseFloat(String(region.total_revenue_usd || 0)),
      totalOutstanding: a.totalOutstanding + parseFloat(String(region.total_outstanding_usd || 0)),
      totalInvoiced: a.totalInvoiced + parseFloat(String(region.total_invoiced_usd || 0)),
      totalInvoicedOriginal:
        a.totalInvoicedOriginal +
        parseFloat(String(region.total_invoiced_original_currency || 0)),
    }),
    {
      totalClients: 0,
      activeClients: 0,
      totalRevenue: 0,
      totalOutstanding: 0,
      totalInvoiced: 0,
      totalInvoicedOriginal: 0,
    }
  )
  if (unique > 0) acc.totalClients = unique
  return acc
}

export function buildRegionChartRows(filteredData: RegionalPerformanceRow[]): RegionChartRow[] {
  const regionAggregated = filteredData.reduce<
    Record<
      string,
      {
        region: string
        revenue: number
        outstanding: number
        totalClients: number
        totalInvoiced: number
        clientListSummary: string[]
      }
    >
  >((acc, item) => {
    if (!item?.region) return acc
    const region = item.region
    if (!acc[region]) {
      acc[region] = {
        region,
        revenue: 0,
        outstanding: 0,
        totalClients: 0,
        totalInvoiced: 0,
        clientListSummary: [],
      }
    }
    acc[region].revenue += parseFloat(String(item.total_revenue_usd || 0))
    acc[region].outstanding += parseFloat(String(item.total_outstanding_usd || 0))
    acc[region].totalInvoiced += parseFloat(String(item.total_invoiced_usd || 0))
    acc[region].totalClients = Math.max(acc[region].totalClients, item.total_clients || 0)
    if (item.client_list_summary) acc[region].clientListSummary.push(item.client_list_summary)
    return acc
  }, {})

  return Object.values(regionAggregated)
    .map(r => ({
      region: r.region,
      regionName: REGION_DISPLAY_NAMES[r.region] || r.region,
      revenue: r.revenue,
      outstanding: r.outstanding,
      totalClients: r.totalClients,
      collectionRate: r.totalInvoiced > 0 ? (r.revenue / r.totalInvoiced) * 100 : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue)
}

function regionGroupKey(raw: string): string {
  if (raw === 'GB' || raw === 'UK') return 'UK'
  return raw
}

export function buildRegionSummaryRows(
  timeFiltered: RegionalPerformanceRow[]
): RegionSummaryRow[] {
  type Acc = {
    revenue: number
    outstanding: number
    invoiced: number
    revenueOriginal: number
    invoicedOriginal: number
    summaries: string[]
    maxReportedClients: number
    apiRegionCurrency?: string
  }

  const map = new Map<string, Acc>()

  for (const item of timeFiltered) {
    if (!item?.region) continue
    const key = regionGroupKey(item.region)
    let a = map.get(key)
    if (!a) {
      a = {
        revenue: 0,
        outstanding: 0,
        invoiced: 0,
        revenueOriginal: 0,
        invoicedOriginal: 0,
        summaries: [],
        maxReportedClients: 0,
      }
      map.set(key, a)
    }
    a.revenue += parseFloat(String(item.total_revenue_usd || 0))
    a.outstanding += parseFloat(String(item.total_outstanding_usd || 0))
    a.invoiced += parseFloat(String(item.total_invoiced_usd || 0))
    a.revenueOriginal += parseFloat(String(item.total_paid_original_currency ?? 0))
    a.invoicedOriginal += parseFloat(String(item.total_invoiced_original_currency ?? 0))
    if (item.client_list_summary) a.summaries.push(item.client_list_summary)
    a.maxReportedClients = Math.max(a.maxReportedClients, item.total_clients || 0)
    if (!a.apiRegionCurrency && item.region_currency) {
      const c = String(item.region_currency).trim().toUpperCase()
      if (/^[A-Z]{3}$/.test(c)) a.apiRegionCurrency = c
    }
  }

  const totalRev = [...map.values()].reduce((s, x) => s + x.revenue, 0)

  const rows: RegionSummaryRow[] = []
  for (const [key, a] of map) {
    const unique = new Set<string>()
    for (const cl of a.summaries) {
      for (const c of parseClientListSummary(cl)) {
        if (c.clientId) unique.add(c.clientId)
      }
    }
    const uniqueClients =
      unique.size > 0 ? unique.size : a.maxReportedClients

    const mappedCcy = REGION_LOCAL_CURRENCY[key] ?? 'USD'
    let displayCurrency = a.apiRegionCurrency || mappedCcy
    if (!/^[A-Z]{3}$/.test(displayCurrency)) displayCurrency = 'USD'

    const revOrig = a.revenueOriginal
    const invOrig = a.invoicedOriginal
    let revenueDisplay = a.revenue
    let invoicedDisplay = a.invoiced
    let collectionRateDisplay =
      a.invoiced > 0 ? (a.revenue / a.invoiced) * 100 : 0

    if (displayCurrency !== 'USD') {
      const missingOrigWhileUsd =
        revOrig === 0 && invOrig === 0 && (a.revenue > 0 || a.invoiced > 0)
      if (missingOrigWhileUsd) {
        displayCurrency = 'USD'
        revenueDisplay = a.revenue
        invoicedDisplay = a.invoiced
        collectionRateDisplay =
          a.invoiced > 0 ? (a.revenue / a.invoiced) * 100 : 0
      } else {
        revenueDisplay = revOrig
        invoicedDisplay = invOrig
        collectionRateDisplay =
          invOrig > 0 ? (revOrig / invOrig) * 100 : collectionRateDisplay
      }
    }

    rows.push({
      selectionKey: key,
      displayCode: key === 'UK' ? 'UK' : key,
      regionName: REGION_DISPLAY_NAMES[key] || key,
      revenue: a.revenue,
      outstanding: a.outstanding,
      totalInvoiced: a.invoiced,
      collectionRate: a.invoiced > 0 ? (a.revenue / a.invoiced) * 100 : 0,
      uniqueClients,
      shareOfRevenuePct: totalRev > 0 ? (a.revenue / totalRev) * 100 : 0,
      displayCurrency,
      revenueDisplay,
      invoicedDisplay,
      collectionRateDisplay,
    })
  }

  return rows.sort((x, y) => y.revenue - x.revenue)
}

export function aggregateClientsAcrossMonths(
  clientListItems: string[]
): { clients: ParsedRegionalClient[]; tierBreakdown: Record<string, number> } {
  const all: ParsedRegionalClient[] = []
  for (const cl of clientListItems) {
    all.push(...parseClientListSummary(cl))
  }
  const clientAggregates: Record<string, ParsedRegionalClient & { monthCount: number }> = {}
  for (const client of all) {
    const key = client.name.trim()
    if (!clientAggregates[key]) {
      clientAggregates[key] = { ...client, monthCount: 0 }
    }
    clientAggregates[key].revenueUSD += client.revenueUSD
    clientAggregates[key].invoicedUSD += client.invoicedUSD || 0
    clientAggregates[key].revenueOriginal += client.revenueOriginal
    clientAggregates[key].invoicedOriginal += client.invoicedOriginal
    clientAggregates[key].outstanding += client.outstanding
    clientAggregates[key].outstandingOriginal += client.outstandingOriginal
    clientAggregates[key].monthCount += 1
  }
  const aggregatedClients = Object.values(clientAggregates).sort(
    (a, b) => b.revenueUSD - a.revenueUSD
  )
  const tierBreakdown = { enterprise: 0, strategic: 0, standard: 0, small: 0 }
  for (const client of aggregatedClients) {
    const t = (client.tier || '').toLowerCase()
    if (t === 'enterprise') tierBreakdown.enterprise++
    else if (t === 'strategic') tierBreakdown.strategic++
    else if (t === 'standard') tierBreakdown.standard++
    else if (t === 'small') tierBreakdown.small++
  }
  return { clients: aggregatedClients, tierBreakdown }
}

/** Unique clients per logical region (UK+GB merged) for inline table drill-down. */
export function buildClientsByRegionKey(
  timeFiltered: RegionalPerformanceRow[]
): Record<string, ParsedRegionalClient[]> {
  const summaries = buildRegionSummaryRows(timeFiltered)
  const out: Record<string, ParsedRegionalClient[]> = {}
  for (const s of summaries) {
    const slice = filterByRegion(timeFiltered, s.selectionKey)
    const parts: string[] = []
    for (const r of slice) {
      if (r.client_list_summary) parts.push(r.client_list_summary)
    }
    if (parts.length === 0) {
      out[s.selectionKey] = []
      continue
    }
    const { clients } = aggregateClientsAcrossMonths(parts)
    out[s.selectionKey] = clients
  }
  return out
}

export function buildSelectedRegionAggregate(
  regionFilteredData: RegionalPerformanceRow[],
  selectedRegion: string
): SelectedRegionAggregate | null {
  if (selectedRegion === 'all' || regionFilteredData.length === 0) return null

  const selectedRegionData = regionFilteredData.reduce<SelectedRegionAggregate | null>(
    (acc, item) => {
      if (!item?.region) return acc
      if (!acc) {
        return {
          region: item.region,
          total_clients: 0,
          enterprise_clients: item.enterprise_clients,
          strategic_clients: item.strategic_clients,
          standard_clients: item.standard_clients,
          small_clients: item.small_clients,
          total_revenue_usd: 0,
          total_outstanding_usd: 0,
          total_invoiced_usd: 0,
          total_invoiced_original_currency: 0,
          revenue_market_share_percent: item.revenue_market_share_percent,
          clients_with_outstanding: item.clients_with_outstanding,
          client_list_summary: '',
          clientListItems: item.client_list_summary ? [item.client_list_summary] : [],
        }
      }
      acc.total_clients = Math.max(acc.total_clients, item.total_clients || 0)
      if (item.enterprise_clients !== undefined && acc.enterprise_clients === undefined) {
        acc.enterprise_clients = item.enterprise_clients
        acc.strategic_clients = item.strategic_clients
        acc.standard_clients = item.standard_clients
        acc.small_clients = item.small_clients
      }
      acc.total_revenue_usd += parseFloat(String(item.total_revenue_usd || 0))
      acc.total_outstanding_usd += parseFloat(String(item.total_outstanding_usd || 0))
      acc.total_invoiced_usd += parseFloat(String(item.total_invoiced_usd || 0))
      acc.total_invoiced_original_currency += parseFloat(
        String(item.total_invoiced_original_currency || 0)
      )
      if (item.client_list_summary) acc.clientListItems.push(item.client_list_summary)
      return acc
    },
    null
  )

  if (!selectedRegionData) return null

  selectedRegionData.region = selectedRegion

  if (regionFilteredData.length === 1) {
    selectedRegionData.total_clients =
      regionFilteredData[0].total_clients || selectedRegionData.total_clients
  } else {
    const uniqueRegionClientIds = new Set<string>()
    for (const item of regionFilteredData) {
      if (!item.client_list_summary) continue
      for (const c of parseClientListSummary(item.client_list_summary)) {
        if (c.clientId) uniqueRegionClientIds.add(c.clientId)
      }
    }
    if (uniqueRegionClientIds.size > 0) {
      selectedRegionData.total_clients = uniqueRegionClientIds.size
    } else {
      selectedRegionData.total_clients = Math.max(
        ...regionFilteredData.map(r => r.total_clients || 0)
      )
    }
  }

  if (selectedRegionData.clientListItems.length > 1) {
    const { clients, tierBreakdown } = aggregateClientsAcrossMonths(
      selectedRegionData.clientListItems
    )
    selectedRegionData.enterprise_clients = tierBreakdown.enterprise
    selectedRegionData.strategic_clients = tierBreakdown.strategic
    selectedRegionData.standard_clients = tierBreakdown.standard
    selectedRegionData.small_clients = tierBreakdown.small
    selectedRegionData.aggregatedClients = clients
    selectedRegionData.client_list_summary = 'aggregated'
  } else if (selectedRegionData.clientListItems.length === 1) {
    selectedRegionData.client_list_summary = selectedRegionData.clientListItems[0]
  }

  return selectedRegionData
}

export function regionHasActivity(regionData: RegionalPerformanceRow[]): boolean {
  return regionData.some(
    item =>
      (item.total_clients && item.total_clients > 0) ||
      parseFloat(String(item.total_invoiced_usd || 0)) > 0 ||
      parseFloat(String(item.total_outstanding_usd || 0)) > 0 ||
      parseFloat(String(item.total_invoices_raised || 0)) > 0
  )
}
